import { ActionIcon, Box, Button, Divider, Flex, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useDebouncedState } from '@mantine/hooks';
import { IconPlus as AddIcon, IconEye as ViewIcon } from '@tabler/icons-react';
import { ChangeEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { MinStdContext } from '@/module/MinimalStandard/components/MinStdContext';
import { IndicatorItemOption } from '@/module/MinimalStandard/model/MinStdTypes';

import { NoContentPlaceholder } from '@/core/components';

interface BaseMinStdSelectModalProps<T> {
  close: () => void;
  list: IndicatorItemOption<T>[];
  renderItem: (item: T) => ReactNode;

  onSelect: (item: T) => void;
  onView?: (item: T) => void;

  getId: (item: T) => number;

  label: string;

  selectedIds: number[];
  onFilter: (item: T, search: string) => boolean;
}

export function useMinStdSearch<T>(
  possibleOptions: IndicatorItemOption<T>[],
  selectedIds: number[],
  getId: (item: T) => number,
  onFilter: (item: T, search: string) => boolean,
) {
  const [value, setValue] = useDebouncedState('', 200);
  const selectedMap: Record<number, boolean> = selectedIds.reduce(
    (acc, next) => {
      acc[next] = true;
      return acc;
    },
    {} as Record<number, boolean>,
  );
  const options = possibleOptions.filter(op => !selectedMap[getId(op.option)]).filter(op => onFilter(op.option, value));

  return {
    searchProps: {
      defaultValue: value,
      onChange: (event: ChangeEvent<HTMLInputElement>) => setValue(event.currentTarget.value),
    },
    options,
  };
}

export function BaseMinStdSelectModal<T>(props: BaseMinStdSelectModalProps<T>) {
  const { close, list, renderItem, label, onSelect, getId, onView, onFilter, selectedIds } = props;
  const { searchProps, options } = useMinStdSearch(list, selectedIds, getId, onFilter);
  const { t } = useTranslation();

  const handleSelect = (item: T) => {
    onSelect(item);
    close();
  };

  return (
    <Modal
      size="70vw"
      opened
      onClose={close}
      withCloseButton={false}
    >
      <Box p="sm">
        <Text
          fw="500"
          size="lg"
        >
          {label}
        </Text>
        <TextInput
          label="caută după titlu și autori"
          {...searchProps}
        />
      </Box>
      <Box
        mah="70vh"
        style={{ overflow: 'scroll' }}
      >
        {options.length === 0 && <NoContentPlaceholder title="Nu exista date" />}
        <Stack p="sm">
          {options.map(({ option, mismatchedConditionType, context, selectable }) => (
            <Flex
              align="center"
              key={getId(option)}
            >
              <Stack>
                <ActionIcon
                  mr="5px"
                  onClick={() => handleSelect(option)}
                  disabled={!selectable}
                  variant="filled"
                  aria-label={t('button.add')}
                >
                  <AddIcon
                    style={{ width: '70%', height: '70%' }}
                    stroke={1.5}
                  />
                </ActionIcon>
                <ActionIcon
                  mr="5px"
                  onClick={onView ? () => onView(option) : undefined}
                  variant="filled"
                  aria-label={t('button.add')}
                >
                  <ViewIcon
                    style={{ width: '70%', height: '70%' }}
                    stroke={1.5}
                  />
                </ActionIcon>
              </Stack>

              <Stack
                flex={1}
                gap="4px"
              >
                <Box
                  style={{ opacity: selectable ? 1 : 0.6 }}
                  flex={1}
                >
                  {renderItem(option)}
                </Box>
                {context && <MinStdContext context={context} />}
                {mismatchedConditionType && (
                  <>
                    <Text size="sm">{t(`mismatchedConditionType.${mismatchedConditionType}`)}</Text>
                    <Button
                      onClick={() => handleSelect(option)}
                      size="xs"
                    >
                      Adaugă oricum
                    </Button>
                    <Divider
                      mt="10px"
                      mb="10px"
                    />
                  </>
                )}
              </Stack>
            </Flex>
          ))}
        </Stack>
      </Box>
    </Modal>
  );
}
